import React, { useCallback, useEffect, useState } from "react";
import { BookOpen, History, Lock, Plus } from "lucide-react";
import { toast } from "sonner";
import { api, errMsg } from "@/lib/api";
import { Skeleton } from "@/components/common";
import { PageBar } from "@/components/layout/PageBar";
import { AuditLog } from "@/components/settings/RatesVersionBar";
import { TemplateCard } from "@/components/settings/TextLibrary";
import { TemplateEditor } from "@/components/settings/TemplateEditor";

const detail = (e) => {
  const d = e?.response?.data?.detail;
  return typeof d === "string" ? d : errMsg(e);
};

/**
 * Admin: the versioned library of customer-facing texts. Every text shows status, source and date; historic texts are
 * source material and never become standard on their own. Writes are fail-closed (403) until an explicit admin right exists.
 */
export default function TextsSettingsPage() {
  const [lib, setLib] = useState(null);
  const [editor, setEditor] = useState(null); // {section, template}
  const [saving, setSaving] = useState(false);
  const [showArchived, setShowArchived] = useState(false);

  const load = useCallback(async () => {
    const r = await api.get("/tilbudstekster");
    setLib(r.data);
  }, []);
  useEffect(() => { load().catch((e) => toast.error(errMsg(e))); }, [load]);

  const run = async (fn, ok) => {
    setSaving(true);
    try { await fn(); await load(); if (ok) toast.success(ok); setEditor(null); } catch (e) { toast.error(detail(e)); } finally { setSaving(false); }
  };
  const save = (d) => run(() => (editor.template ? api.post(`/tilbudstekster/${editor.template.tid}/versions`, d) : api.post("/tilbudstekster", { ...d, section: editor.section.key })), editor?.template ? "Ny versjon lagret – den gamle beholdes" : "Teksten er lagt i biblioteket");
  const standard = (t, on) => run(() => api.post(`/tilbudstekster/${t.tid}/standard`, { on }), on ? `«${t.title}» er standard for nye tilbud. Eksisterende saker og revisjoner endres ikke.` : "Standard fjernet");
  const archive = (t, on) => run(() => api.post(`/tilbudstekster/${t.tid}/archive`, { on }), on ? "Arkivert – saker som bruker teksten beholder den" : "Hentet fram igjen");

  const total = lib ? lib.sections.reduce((n, s) => n + s.templates.filter((t) => !t.archived_at).length, 0) : 0;
  return (
    <div className="mx-auto w-full max-w-[1100px] px-5 pb-16 pt-5 sm:px-8" data-testid="texts-settings-page">
      <PageBar title="Tilbudstekster" meta={lib ? `${total} ${total === 1 ? "tekst" : "tekster"} · endringer blir nye versjoner, saker beholder sin` : null} back={{ to: "/settings", label: "Innstillinger" }} testId="texts-bar" className="pb-4">
        <label className="inline-flex h-8 items-center gap-2 rounded-[8px] px-2 text-[13.5px] text-foreground/60 hover:bg-black/[0.035] hover:text-foreground/85"><input type="checkbox" checked={showArchived} onChange={(e) => setShowArchived(e.target.checked)} className="h-3.5 w-3.5" data-testid="texts-show-archived" /> Vis arkiverte</label>
      </PageBar>
      {!lib ? <div className="space-y-4"><Skeleton className="h-16" /><Skeleton className="h-64" /></div> : (
        <>
          {!lib.can_write ? (
            <div className="mb-2 flex items-start gap-2 rounded-[12px] border border-[hsl(var(--v-assumed)/0.45)] bg-[hsl(45_93%_47%/0.08)] px-3 py-2 text-[12.5px]" data-testid="texts-write-blocked">
              <Lock className="mt-[2px] h-4 w-4 shrink-0 text-[hsl(var(--v-assumed))]" />
              <div><span className="font-semibold">Lagring er sperret.</span> {lib.write_block_reason} Biblioteket kan leses, og tekstene kan velges og tilpasses på den enkelte sak under Tilbud → Rediger.</div>
            </div>
          ) : null}
          {lib.sections.map((s) => {
            const rows = s.templates.filter((t) => showArchived || !t.archived_at);
            return (
              <section key={s.key} id={`texts-${s.key}`} className="scroll-mt-6 grid grid-cols-1 gap-5 border-t border-black/[0.08] py-8 lg:grid-cols-[240px_minmax(0,1fr)]" data-testid={`texts-section-${s.key}`}>
                <div>
                  <h2 className="flex items-center gap-2 text-[14.5px] font-semibold"><BookOpen className="h-4 w-4 text-muted-foreground" strokeWidth={1.9} /> {s.label}</h2>
                  <p className="mt-1 text-[13px] leading-relaxed text-muted-foreground">{s.hint}</p>
                  <p className="mt-2 text-[12px] text-muted-foreground" data-testid={`texts-section-${s.key}-standard`}>{s.standard ? <>Standard: <span className="font-medium text-foreground/80">«{s.standard.title}» v{s.standard.version}</span></> : "Ingen standard satt – velges per sak."}</p>
                  <button type="button" onClick={() => setEditor({ section: s, template: null })} className="mt-3 inline-flex h-8 items-center gap-1.5 rounded-full border border-black/[0.1] bg-white px-3 text-[12.5px] font-medium hover:border-black/[0.25]" data-testid={`texts-section-${s.key}-new`}><Plus className="h-3.5 w-3.5" /> Ny tekst</button>
                </div>
                <div className="min-w-0">
                  {rows.length ? <ul className="space-y-3">{rows.map((t) => <TemplateCard key={t.id} t={t} canWrite={lib.can_write} onRevise={(x) => setEditor({ section: s, template: x })} onStandard={standard} onArchive={archive} />)}</ul>
                    : <p className="rounded-[14px] border border-dashed border-black/[0.12] px-4 py-6 text-center text-[13px] text-muted-foreground" data-testid={`texts-section-${s.key}-empty`}>Ingen tekst i biblioteket ennå. Skrives per sak til noen legger inn en bekreftet standard.</p>}
                </div>
              </section>
            );
          })}
          <section className="grid grid-cols-1 gap-5 border-t border-black/[0.08] py-8 lg:grid-cols-[240px_minmax(0,1fr)]" data-testid="texts-section-audit">
            <div><h2 className="flex items-center gap-2 text-[14.5px] font-semibold"><History className="h-4 w-4 text-muted-foreground" strokeWidth={1.9} /> Logg</h2><p className="mt-1 text-[13px] leading-relaxed text-muted-foreground">Hvem endret hva, når – inkludert kildeimport og standardvalg.</p></div>
            <AuditLog entries={lib.audit} />
          </section>
        </>
      )}
      <TemplateEditor open={Boolean(editor)} onClose={() => setEditor(null)} section={editor?.section} template={editor?.template} onSave={save} saving={saving} canWrite={lib?.can_write} />
    </div>
  );
}
